import React, { useCallback } from 'react';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/styles';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';

import styles from '../styles/portNumberStep';
import { actions as reduxActions } from '../reducers';
import validateSchema from '../actions/validateSchema';
import InputRow from '../../../partials/dataRows/InputRow';
import CheckboxRow from '../../../partials/dataRows/CheckboxRow';
import DatePickerRow from '../../../partials/dataRows/DatepickerRow';
import { attorneySubmit } from '../actions/attorneyRequest';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import ConfirmDialog from '../../../partials/confirmationDialog/ConfirmDialog';
import CustomIconButton from '../../../partials/tableCustomButtons/CustomIconButton';
import CurrentOwnerInfo from './CurrentOwnerInfo';
import withSettings from '../../../utils/withSettings';
import { translate } from '../../../i18n/I18nProvider';

const CUSTOM_STYLES = {
  moveNumberCheckbox: {
    root: {
      width: '400px',
      paddingLeft: '30px',
      alignItems: 'flex-start'
    },
    label: {
      width: '110px'
    }
  }
};

export function PortNumberStep({
  classes,
  resestUserWizard,
  handleBack,
  handleNext,
  setUserData,
  thirdStepData,
  attorneySubmit,
  attorneySend,
  settings,
  onCloseAttempt
}) {
  const onSubmit = useCallback(
    async values => {
      await attorneySubmit({
        userData: { ...values },
        setUserData: () => setUserData(values, 'thirdStep'),
        handleNext
      });
    },
    // eslint-disable-next-line
    [attorneySend]
  );

  const onBackClick = useCallback(
    () => {
      setUserData(null, 'thirdStep');
      return handleBack();
    },
    // eslint-disable-next-line
    []
  );

  return (
    <Formik
      initialValues={thirdStepData}
      enableReinitialize
      validateOnChange={false}
      onSubmit={onSubmit}
      validationSchema={validateSchema({}).portStep}
    >
      {({
        handleSubmit,
        errors,
        values,
        handleBlur,
        handleChange,
        setFieldValue
      }) => {
        const fieldProps = {
          errors,
          values,
          handleBlur,
          handleChange,
          setFieldValue
        };
        return (
          <Form onSubmit={handleSubmit}>
            <div className={classes.root}>
              <div className={classes.sectionContainer}>
                <div className={classes.sectionLabel}>
                  {translate('REQUEST_NEW_USER.MOVE_NUMBER')}
                </div>
                <div className={classes.dataContainer}>
                  <div className={classes.moveNumber}>
                    <InputRow
                      label="REQUEST_NEW_USER.MOVE_MOBILE"
                      name="portMobileNumber"
                      disabled={true}
                      {...fieldProps}
                    />
                    <DatePickerRow
                      label="REQUEST_NEW_USER.MOVE_DATA"
                      name="move_number_date"
                      disabled={values.is_old_subscripion_date}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      infoIcon={true}
                      infoIconTooltipContent={
                        <span>
                          <strong>
                            {translate('TOOLTIP.MOVE_NUMBER_DATE_TITLE_1')}
                          </strong>
                          <br />
                          {translate('TOOLTIP.MOVE_NUMBER_DATE_DESC_1')}
                          <br />
                          <br />
                          <strong>
                            {translate('TOOLTIP.MOVE_NUMBER_DATE_TITLE_2')}
                          </strong>
                          <br />
                          {translate('TOOLTIP.MOVE_NUMBER_DATE_DESC_2')}
                        </span>
                      }
                      daysAhead={2}
                    />
                    <CheckboxRow
                      label="REQUEST_NEW_USER.WHEN_OLD_SUBSCRIPTION"
                      name="is_old_subscripion_date"
                      customStyles={CUSTOM_STYLES.moveNumberCheckbox}
                      customCheckFunction={e => {
                        handleChange(e);
                        if (e.target.checked) {
                          setFieldValue('move_number_date', null);
                        }
                      }}
                      {...fieldProps}
                    />
                  </div>
                  <div>
                    <InputRow
                      label="REQUEST_NEW_USER.CURRENT_SIM_NUMBER"
                      name="current_sim_card_number"
                      {...fieldProps}
                    />
                    <InputRow
                      name="current_operator"
                      label="REQUEST_NEW_USER.CURRENT_OPERATOR"
                      disabled
                      {...fieldProps}
                    />
                  </div>
                </div>
              </div>
              <div className={classes.sectionContainer}>
                <div className={classes.sectionLabel}>
                  {translate('REQUEST_NEW_USER.CURRENT_OWNER')}
                  <ConfirmDialog
                    dialogTitleIntlId={
                      'REQUEST_NEW_USER.CURRENT_OWNER_INFO_TITLE'
                    }
                    dialogContentComponent={<CurrentOwnerInfo />}
                    actionButtonsEnabled={false}
                  >
                    <CustomIconButton
                      iconTooltipIntlKey="TOOLTIP.CLICK_FOR_INFOTOOLTIP"
                      iconClassName="fas fa-info-circle"
                      customStyles={{
                        color: settings.colors.primary,
                        margin: '5px 20px'
                      }}
                    />
                  </ConfirmDialog>
                </div>
                <div className={classes.dataContainer}>
                  <div>
                    <InputRow
                      name="name"
                      label="REQUEST_NEW_USER.COMPANY_NAME"
                      {...fieldProps}
                    />
                    <InputRow
                      label="REQUEST_NEW_USER.CVR_NUMBER"
                      name="cvr"
                      {...fieldProps}
                    />
                  </div>
                  <div>
                    <InputRow
                      label="REQUEST_NEW_USER.ADDRESS"
                      name="address"
                      {...fieldProps}
                    />
                    <InputRow
                      label="REQUEST_NEW_USER.POSTAL_NO"
                      name="postal_code"
                      {...fieldProps}
                    />
                    <InputRow
                      label="REQUEST_NEW_USER.CITY"
                      name="city"
                      {...fieldProps}
                    />
                  </div>
                </div>
              </div>
              <div>
                <InputRow
                  label="REQUEST_NEW_USER.RECIPIENT_EMAIL"
                  name="recipient_email"
                  disaled={true}
                  {...fieldProps}
                />
                <InputRow
                  label="REQUEST_NEW_USER.OTHER_RECIPIENT_EMAIL"
                  name="other_recipient_email"
                  {...fieldProps}
                />
              </div>
            </div>
            <div className={classes.footer}>
              <PrimaryButton onClick={onBackClick}>
                {translate('BUTTON.BACK')}
              </PrimaryButton>
              <PrimaryButton onClick={handleSubmit}>
                {translate('BUTTON.PRINT_ATTORNEY')}
              </PrimaryButton>
              <SecondaryButton onClick={onCloseAttempt || resestUserWizard}>
                {translate('BUTTON.CANCEL')}
              </SecondaryButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

const mapStatesToProps = ({ requestUserWizard }) => {
  const {
    userData: { firstStep, secondStep, thirdStep }
  } = requestUserWizard;

  const thirdStepData = {
    ...thirdStep,
    recipient_email: firstStep.email,
    portMobileNumber: secondStep.mobile_number
  };
  return {
    thirdStepData
  };
};

const mapDispatchToProps = {
  setUserData: reduxActions.setUserData,
  resestUserWizard: reduxActions.resestUserWizard,
  attorneySubmit
};

export default flowRight(
  withSettings,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(PortNumberStep);
